import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUnlock, FiGraduationCap, FiAward, FiZap, FiFlag, FiBrain, FiHeart, FiTarget } = FiIcons;

const About = () => {
  const [showRandomFacts, setShowRandomFacts] = useState(false);

  const credentials = [
    {
      icon: FiGraduationCap,
      title: "Academic Excellence",
      description: "Trained at Purdue University, ranked globally among the top speech-language pathology programs.",
      color: "bg-blue-100 text-blue-600"
    },
    {
      icon: FiAward,
      title: "Elite Recognition", 
      description: "Awarded the David Knox Fellowship, an honor given to less than 0.02% of applicants for exceptional promise in the field.",
      color: "bg-accent-gold/20 text-orange-600"
    },
    {
      icon: FiZap,
      title: "Scientific Roots",
      description: "Former NASA scientist with a strong background in brain and cognitive sciences.",
      color: "bg-accent-purple/20 text-purple-600"
    },
    {
      icon: FiFlag,
      title: "Presidential Award",
      description: "Recipient of the Outstanding Student Award from President Obama.",
      color: "bg-red-100 text-red-600"
    },
    {
      icon: FiBrain,
      title: "Specialized Expertise",
      description: "A recognized specialist in traumatic brain injury and executive function — uniquely qualified to help you upgrade your brain's sleep system.",
      color: "bg-accent-magenta/20 text-accent-magenta"
    },
    {
      icon: FiHeart,
      title: "Lived Experience",
      description: "Sanna understands firsthand the struggle of sleep and EF challenges, bringing empathy and insight to every coaching session.",
      color: "bg-pink-100 text-pink-600"
    }
  ];

  const randomFacts = [
    { text: "Mildly obsessed with French fries", id: "fries" },
    { text: "Whodunit lover", id: "whodunit" }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-gray-50 via-white to-accent-light/30">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Guarantee Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <h2 className="text-3xl lg:text-5xl font-playfair font-bold text-gray-900 mb-8">
            Meaningful Improvement in 30 Days, Guaranteed.
          </h2>
          <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-xl max-w-4xl mx-auto">
            <div className="flex justify-center mb-6">
              <div className="bg-accent-magenta/10 p-6 rounded-full">
                <SafeIcon icon={FiTarget} className="w-12 h-12 text-accent-magenta" />
              </div>
            </div>
            <p className="text-xl text-gray-700 font-raleway leading-relaxed">
              <strong>Our Promise:</strong> We believe in our coaching. If you don't notice better sleep or sharper focus within 30 days, we'll work with you until you do — or offer a refund. Because your well-being is that important.
            </p>
          </div>
        </motion.div>

        {/* About Sanna Section */}
        <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
          {/* Random Facts Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="bg-white rounded-3xl p-8 shadow-xl">
              <div className="text-center mb-6">
                <button
                  onClick={() => setShowRandomFacts(!showRandomFacts)}
                  className="flex items-center gap-2 mx-auto bg-accent-magenta text-white px-6 py-3 rounded-full font-semibold hover:bg-accent-magenta/90 transition-all duration-200"
                >
                  <SafeIcon icon={FiUnlock} className="w-5 h-5" />
                  {showRandomFacts ? 'Hide' : 'Unlock'} random facts about Sanna
                </button>
              </div>

              {/* Random Facts with Arrows */}
              <AnimatePresence>
                {showRandomFacts && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-4"
                  >
                    {randomFacts.map((fact, index) => (
                      <motion.div
                        key={fact.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        className="relative bg-accent-light/50 p-4 rounded-xl"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-accent-magenta rounded-full"></div>
                          <p className="text-gray-700 font-raleway">{fact.text}</p>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Bio Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div>
              <h3 className="text-3xl lg:text-4xl font-playfair font-bold text-gray-900 mb-4">
                Meet <span className="text-accent-magenta">Sanna</span> Darwish, M.S. CCC-SLP
              </h3>
              <p className="text-xl text-gray-600 font-raleway mb-6">
                Your Executive Function Sleep Coach
              </p>
            </div>
            <div className="space-y-6 text-lg text-gray-700 font-raleway leading-relaxed">
              <p>
                This is not your average sleep coaching. It's science, skill, and heart — all working together to get your brain well-rested and performing at its best.
              </p>
              <p className="font-semibold text-accent-magenta">
                Let's get you sleeping better — and thinking clearer — starting today.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Credentials Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {credentials.map((credential, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${credential.color} mb-4`}>
                  <SafeIcon icon={credential.icon} className="w-6 h-6" />
                </div>
                <h4 className="text-lg font-playfair font-bold text-gray-900 mb-3">
                  {credential.title}
                </h4>
                <p className="text-gray-600 font-raleway text-sm leading-relaxed">
                  {credential.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;